import React, { useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import {
    ResponsiveContainer,
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
} from 'recharts'
import { COLLECTION_AGGREGATES } from './queries'

function fmtMoney(v) {
    if (v == null) return '—'
    const n = typeof v === 'string' ? Number(v) : v
    if (Number.isNaN(n)) return '—'
    return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
}

// Collected tile: append " (X%)" using collected / (collected + outstanding)
// — i.e. the share of total billable that's actually been paid in.
function fmtCollected(collected, outstanding) {
    const money = fmtMoney(collected)
    if (money === '—') return money
    const c = Number(collected) || 0
    const o = Number(outstanding) || 0
    const total = c + o
    if (total <= 0) return money
    const pct = Math.round((c / total) * 100)
    return `${money} (${pct}%)`
}

function isoDaysAgo(days, ref = new Date()) {
    const d = new Date(ref)
    d.setDate(d.getDate() - days)
    return d.toISOString().slice(0, 10)
}
function todayIso(ref = new Date()) {
    return ref.toISOString().slice(0, 10)
}

const PRESETS = [
    { label: '7d', days: 7 },
    { label: '30d', days: 30 },
    { label: '90d', days: 90 },
]

function Tile({ label, value }) {
    return (
        <div className="cd-tile">
            <div className="label">{label}</div>
            <div className="value">{value}</div>
        </div>
    )
}

const CHART_AXIS = { fontSize: 10, fill: '#9ca3af' }

export default function AdminOverview() {
    const [startDate, setStartDate] = useState(() => isoDaysAgo(30))
    const [endDate, setEndDate] = useState(() => todayIso())

    const variables = useMemo(
        () => ({ startDate: startDate || null, endDate: endDate || null }),
        [startDate, endDate],
    )

    const { data, loading } = useQuery(COLLECTION_AGGREGATES, {
        variables,
        fetchPolicy: 'cache-and-network',
    })

    const applyPreset = (days) => {
        setStartDate(isoDaysAgo(days))
        setEndDate(todayIso())
    }

    const a = data?.collectionAggregates
    const trendData = (a?.collectedTrend ?? []).map((p) => ({
        date: p.date,
        amount: Number(p.amount),
    }))
    const agingData = a
        ? [
              { label: '0-7d', value: a.agingBuckets.d07 },
              { label: '7-14d', value: a.agingBuckets.d714 },
              { label: '14-30d', value: a.agingBuckets.d1430 },
              { label: '30d+', value: a.agingBuckets.d30Plus },
          ]
        : []

    const isActivePreset = (days) =>
        startDate === isoDaysAgo(days) && endDate === todayIso()

    return (
        <div className="cd-overview">
            <div className="cd-overview-window">
                <span className="cd-overview-window-label">Window</span>
                <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    aria-label="Start date"
                    max={endDate || undefined}
                />
                <span className="cd-overview-window-dash">→</span>
                <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    aria-label="End date"
                    min={startDate || undefined}
                    max={todayIso()}
                />
                <div className="cd-overview-window-presets">
                    {PRESETS.map((p) => (
                        <button
                            key={p.label}
                            type="button"
                            className={isActivePreset(p.days) ? 'active' : ''}
                            onClick={() => applyPreset(p.days)}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            {!a && loading ? (
                <div className="cd-empty">Loading overview…</div>
            ) : a ? (
                <>
                    <div className="cd-tiles">
                        <Tile label="Outstanding" value={fmtMoney(a.outstandingTotal)} />
                        <Tile
                            label="Collected"
                            value={fmtCollected(a.collectedTotal, a.outstandingTotal)}
                        />
                        <Tile label="Open cases" value={a.openCount} />
                        <Tile label="Closed cases" value={a.closedCount} />
                    </div>

                    <div className="cd-charts">
                        <div className="cd-card">
                            <h3>Collected · last 30 days</h3>
                            <div style={{ height: 200 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart
                                        data={trendData}
                                        margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                                        <XAxis dataKey="date" tick={CHART_AXIS} stroke="#e6e8ec" />
                                        <YAxis tick={CHART_AXIS} stroke="#e6e8ec" />
                                        <Tooltip />
                                        <Line
                                            type="monotone"
                                            dataKey="amount"
                                            stroke="#10b981"
                                            strokeWidth={2.5}
                                            dot={false}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="cd-card">
                            <h3>Aging · open cases</h3>
                            <div style={{ height: 200 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={agingData}
                                        margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" />
                                        <XAxis dataKey="label" tick={CHART_AXIS} stroke="#e6e8ec" />
                                        <YAxis tick={CHART_AXIS} stroke="#e6e8ec" />
                                        <Tooltip />
                                        <Bar dataKey="value" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </>
            ) : null}
        </div>
    )
}
